import { forwardRef, Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { CustomerInfoLoadIfNormalUserGuard } from 'app/core/guards/customer-info-load-if-normal-user.guard';
import { DependencyTreeCanActivate } from 'app/core/guards/dependency-tree.guard';
import { OrderFilterValuesLoadGuard } from 'app/core/guards/order-filter-values-load.guard';
import { OrderSandbox } from 'app/core/sandboxes/order.sandbox';
import { AuthorizationGuard } from 'app/core/store/session/authorization/authorization.resolve';
import { logger } from 'app/core/util/logger';
import { Observable, of } from 'rxjs';
import * as fromRoot from '..';
import { TemplateRestService } from '../../services/rest/template.rest.service';
import { CDFlorderType, CDTo } from '../contract-details/contract-details.interface';
import { OrderDetailTO } from '../edit-florder-detail/florder-detail.interface';
import { goAction } from '../router/router.actions';
import { first, switchMap } from 'rxjs/operators';

/**
 * Guard to async load an order before following a route
 */
@Injectable({ providedIn: 'root' })
export class CreateOrderFromTemplateGuard implements CanActivate, DependencyTreeCanActivate {
  private templateRestService: TemplateRestService;
  private store: Store<fromRoot.RootState>;
  private customerInfoLoadIfNormalUserGuard: CustomerInfoLoadIfNormalUserGuard;
  private sandbox: OrderSandbox;

  constructor(
    @Inject(forwardRef(() => Store)) store,
    @Inject(forwardRef(() => CustomerInfoLoadIfNormalUserGuard)) customerInfoLoadIfNormalUserGuard,
    @Inject(forwardRef(() => TemplateRestService)) templateRestService,
    @Inject(forwardRef(() => OrderSandbox)) sandbox
  ) {
    this.store = store;
    this.customerInfoLoadIfNormalUserGuard = customerInfoLoadIfNormalUserGuard;
    this.templateRestService = templateRestService;
    this.sandbox = sandbox;
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    logger.debug(`In CreateOrderFromTemplateGuard`);

    return this.customerInfoLoadIfNormalUserGuard.canActivate(route, state).pipe(
      first(),
      switchMap(customerInfoLoaded => {
        return this.store.pipe(
          first(),
          switchMap(state => {
            if (!state.session.authorization.data.ORDER.CREATE) {
              this.store.dispatch(goAction({ path: '/' }));
              return of(false);
            }

            const isTransporter = state.session.userContext.isSuperUser;
            this.sandbox.createOrderFromTemplate({
              templateId: +route.paramMap.get('templateId'),
              date: route.queryParamMap.get('date'),
              customer: isTransporter ? state.session._activeCustomer.data : state.session.selectedCustomer.data,
            });

            return of(false);
          })
        );
      })
    );
  }

  getDependencies(): any {
    return [OrderFilterValuesLoadGuard, AuthorizationGuard];
  }

  getDependencyRouteData(): any {
    return {
      authorization: 'ORDER.CREATE',
    };
  }

  private findTo(florderTypes: CDFlorderType[], template: OrderDetailTO): CDTo {
    const orderType: CDFlorderType = florderTypes.find(orderType => orderType.id === template.orderType);
    const shippingCondition = orderType && orderType.shippingConditions.find(shippingCondition => shippingCondition.id === template.transport);
    const from = shippingCondition && shippingCondition.froms.find(from => from.id === template.from.id);
    return from && from.tos.find(to => to.id === template.to.id);
  }
}
